// src/components/organisms/sidebar/sidebar.jsx
import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { Link, NavLink as NavLinkRRD, useLocation, useHistory } from 'react-router-dom';
import { Button, Collapse, Nav, Navbar, NavbarBrand, NavItem, NavLink } from 'reactstrap';
import AdminsApiV3 from 'api/v3/admins-api-v3.js';
import '@fortawesome/fontawesome-free/css/all.min.css';
import canAccessAuditLogQc from 'utils/auditLogQcAccess';
import 'assets/vendor/nucleo/css/nucleo.css';

const propTypes = {
  toggleSidebar: PropTypes.func,
  isSidebarOpen: PropTypes.bool,
  routes: PropTypes.arrayOf(PropTypes.object),
  logo: PropTypes.shape({
    name: PropTypes.string,
    sup: PropTypes.string,
    innerLink: PropTypes.string,
    outterLink: PropTypes.string,
    imgSrc: PropTypes.string.isRequired,
    imgAlt: PropTypes.string,
  }),
};

const Sidebar = ({ logo, routes, isSidebarOpen, toggleSidebar }) => {
  const [collapseStates, setCollapseStates] = useState({});
  const [hoverLeafKey, setHoverLeafKey] = useState(null);
  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    setCollapseStates({ ...getCollapseStates(routes) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ===== Active helpers (leaf only) =====
  const leafIsActive = (route) => {
    if (!route?.path) return false;
    const target = `${route.layout || ''}${route.path}`;
    return (
      location.pathname === target ||
      (target !== '/' && location.pathname.startsWith(`${target}/`))
    );
  };

  // Section is "active" only to auto-open collapse (no visual highlight)
  const sectionHasActiveChild = (route) =>
    !!route?.views?.some((r) => (r.collapse ? sectionHasActiveChild(r) : leafIsActive(r)));

  // ===== Collapse helpers =====
  const getCollapseInitialState = (routesArr) => {
    for (let i = 0; i < routesArr.length; i++) {
      const r = routesArr[i];
      if (r.collapse && getCollapseInitialState(r.views)) return true;
      if (!r.collapse && leafIsActive(r)) return true;
    }
    return false;
  };

  const getCollapseStates = (routesArr) => {
    let initialState = {};
    routesArr.forEach((route) => {
      if (route.collapse) {
        initialState = {
          [route.state_key]: getCollapseInitialState(route.views),
          ...getCollapseStates(route.views),
          ...initialState,
        };
      }
    });
    return initialState;
  };

  // ===== UI behavior =====
  const onMouseEnterSidenav = () => {
    if (!document.body.classList.contains('g-sidenav-pinned')) {
      document.body.classList.add('g-sidenav-show');
    }
  };
  const onMouseLeaveSidenav = () => {
    if (!document.body.classList.contains('g-sidenav-pinned')) {
      document.body.classList.remove('g-sidenav-show');
    }
  };
  const closeSidenav = () => {
    if (window.innerWidth < 1080) toggleSidebar();
  };

  // ===== Link builders =====

  const getCurrentAdminRole = () => {
    return window.__currentAdmin?.role || ''
  }

  const canAccessRoute = (route) => {
    if (!Array.isArray(route?.allowedRoles) || route.allowedRoles.length === 0) {
      return true
    }

    return route.allowedRoles.includes(getCurrentAdminRole())
  }

  const createLinks = (routesArr) =>
    routesArr.map((route, key) => {
      if (route.redirect || route.invisible) return null;
      if (!canAccessRoute(route)) return null;
      
      if (route.key === 'audit-log-qc' && !canAccessAuditLogQc(window.__currentAdmin)) return null;
      
      // Section header (NO highlight; just toggles children)
      if (route.collapse) {
        const st = { [route.state_key]: !collapseStates[route.state_key] };
        const isOpen = collapseStates[route.state_key] || sectionHasActiveChild(route);

        return (
          <NavItem key={`sec-${key}`}>
            <NavLink
              data-toggle="collapse"
              aria-expanded={isOpen}
              className={classnames({})}
              onClick={(e) => {
                e.preventDefault();
                setCollapseStates((s) => ({ ...s, ...st }));
              }}
              style={{
                backgroundColor: 'transparent',
                color: '#000',
                borderRadius: 8,
                padding: '10px 15px',
                transition: 'all .3s ease',
                cursor: 'pointer',
              }}
            >
              {route.icon ? (
                <>
                  <i className={route.icon} />
                  <span className="nav-link-text">{route.name}</span>
                </>
              ) : route.miniName ? (
                <>
                  <span className="sidenav-mini-icon">{route.miniName}</span>
                  <span className="sidenav-normal">{route.name}</span>
                </>
              ) : (
                <span>{route.name}</span>
              )}
            </NavLink>

            <Collapse isOpen={isOpen}>
              <Nav className="nav-sm flex-column">{createLinks(route.views)}</Nav>
            </Collapse>
          </NavItem>
        );
      }

      // Leaf (submenu item) — ONLY these get green hover/active
      const leafKey = `leaf-${key}`;
      const isActive = leafIsActive(route);

      return (
        <NavItem className={isActive ? 'active' : ''} key={leafKey}>
          <NavLink
            to={`${route.layout || ''}${route.path}`}
            activeClassName="active"
            onClick={closeSidenav}
            tag={NavLinkRRD}
            style={{
              backgroundColor:
                hoverLeafKey === leafKey || isActive ? '#9db8ce' : 'transparent',
              color: hoverLeafKey === leafKey || isActive ? '#fff' : '#000',
              borderRadius: 8,
              padding: '8px 14px',
              transition: 'all .3s ease',
            }}
            onMouseEnter={() => setHoverLeafKey(leafKey)}
            onMouseLeave={() => setHoverLeafKey(null)}
          >
            {route.icon !== undefined ? (
              <>
                <i className={route.icon} />
                <span className="nav-link-text">{route.name}</span>
              </>
            ) : route.miniName !== undefined ? (
              <>
                <span className="sidenav-mini-icon">{route.miniName}</span>
                <span className="sidenav-normal">{route.name}</span>
              </>
            ) : (
              route.name
            )}
          </NavLink>
        </NavItem>
      );
    });

  // ===== actions =====
  const handleLogout = async () => {
    await AdminsApiV3.logout()
      .then(() => {
        history.go('/');
      })
      .catch((error) => {
        if (error?.response?.status === 401) {
          history.go('/');
        } else {
          alert(error.message);
        }
      });
  };

  const handleUpdatePassword = async () => {
    history.push('/app/sales/bagspa/update-password');
  };

  // ===== structure =====
  const ScrollBarInner = () => (
    <div className="scrollbar-inner">
      {/* Header */}
      <div className="sidenav-header d-flex align-items-center">
        <NavbarBrand tag={Link} name={logo.name} sup={logo.sup} to="/">
          <img height={28} src={logo.imgSrc} alt={logo?.imgAlt || 'logo'} />
          <span className="ml-3">{logo.name}</span>
          {logo.sup && <sup className="font-weight-light">{logo.sup}</sup>}
        </NavbarBrand>

        <div className="ml-auto">
          <div
            className={classnames('sidenav-toggler d-none d-xl-block', {
              active: isSidebarOpen,
            })}
            onClick={toggleSidebar}
          >
            <div className="sidenav-toggler-inner">
              <i className="sidenav-toggler-line" />
              <i className="sidenav-toggler-line" />
              <i className="sidenav-toggler-line" />
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="navbar-inner">
        <Collapse navbar isOpen={true}>
          <Nav navbar>{createLinks(routes)}</Nav>

          <hr className="my-3" />

          <Nav className="mb-md-3" navbar>
            <NavItem onClick={handleUpdatePassword}>
              <NavLink>
                <Button outline block color="secondary" size="sm">
                  <span className="nav-link-text">{'Update Password'}</span>
                </Button>
              </NavLink>
            </NavItem>

            <NavItem onClick={handleLogout}>
              <NavLink>
                <Button outline block color="primary" size="sm">
                  <i className="fas fa-sign-out-alt" />
                  <span className="nav-link-text">{'Logout'}</span>
                </Button>
              </NavLink>
            </NavItem>
          </Nav>
        </Collapse>
      </div>
    </div>
  );

  const ScrollBarBody = () => {
    const isPerfectScrollbarSupported = navigator.platform.indexOf('Win') > -1;
    if (isPerfectScrollbarSupported) {
      return (
        <PerfectScrollbar>
          <ScrollBarInner />
        </PerfectScrollbar>
      );
    }
    return <ScrollBarInner />;
  };

  return (
    <Navbar
      className="sidenav navbar-vertical navbar-expand-xs navbar-light bg-white fixed-left"
      onMouseEnter={onMouseEnterSidenav}
      onMouseLeave={onMouseLeaveSidenav}
    >
      <ScrollBarBody />
    </Navbar>
  );
};

Sidebar.defaultProps = {
  routes: [{}],
  toggleSidebar: () => {},
  isSidebarOpen: false,
};

Sidebar.propTypes = propTypes;

export default Sidebar;
